/**
 * Prompt Validation Utilities
 * 
 * Helper functions for validating custom prompts
 */

// Note: EDITABLE_PROMPTS should be passed as parameter when available
// Fallback import is used only when parameter is not provided
import { EDITABLE_PROMPTS as FALLBACK_EDITABLE_PROMPTS } from '../config/reportPrompts';

/**
 * Check if all required placeholders are present in text
 * @param {string} text - Text to check
 * @param {Array<string>} placeholders - Array of required placeholder strings
 * @returns {Array<string>} Array of missing placeholders
 */
export const checkPlaceholders = (text, placeholders) => {
  if (!text || !placeholders || placeholders.length === 0) {
    return [];
  }
  
  const missing = [];
  for (const placeholder of placeholders) {
    if (!text.includes(placeholder)) {
      missing.push(placeholder);
    }
  }
  
  return missing;
};

/**
 * Validate a single prompt
 * @param {string} promptName - Name/key of the prompt
 * @param {string} promptText - Text of the prompt
 * @param {Array<string>} requiredPlaceholders - Required placeholders for this prompt
 * @param {Array} editablePromptsList - Optional list of editable prompts (defaults to imported EDITABLE_PROMPTS)
 * @returns {Object} Validation result with isValid and errors
 */
export const validatePrompt = (promptName, promptText, requiredPlaceholders, editablePromptsList = null) => {
  const errors = [];
  
  // Use provided list or fallback
  let validPromptKeys;
  if (editablePromptsList) {
    validPromptKeys = editablePromptsList.map(p => p.key);
  } else {
    // Fallback to imported EDITABLE_PROMPTS
    validPromptKeys = FALLBACK_EDITABLE_PROMPTS.map(p => p.key);
  }
  
  // Check if prompt name is valid
  if (!validPromptKeys.includes(promptName)) {
    return {
      isValid: false,
      errors: [`Invalid prompt name: ${promptName}. Must be one of: ${validPromptKeys.join(', ')}`]
    };
  }
  
  // Check if prompt text is empty
  if (!promptText || !promptText.trim()) {
    return {
      isValid: false,
      errors: ['Prompt text cannot be empty']
    };
  }
  
  // Check for missing placeholders
  const missingPlaceholders = checkPlaceholders(promptText, requiredPlaceholders);
  if (missingPlaceholders.length > 0) {
    errors.push(`Missing required placeholders: ${missingPlaceholders.join(', ')}`);
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Validate all custom prompts
 * @param {Object} customPrompts - Object mapping prompt names to prompt texts
 * @param {Array} editablePromptsList - Optional list of editable prompts
 * @returns {Object} Validation result with isValid and errors object
 */
export const validateAllPrompts = (customPrompts, editablePromptsList = null) => {
  if (!customPrompts || Object.keys(customPrompts).length === 0) {
    return {
      isValid: true,
      errors: {}
    };
  }
  
  // Use provided list or fallback
  let promptsList;
  if (editablePromptsList) {
    promptsList = editablePromptsList;
  } else {
    promptsList = FALLBACK_EDITABLE_PROMPTS;
  }
  
  const errors = {};
  let allValid = true;
  
  for (const [promptName, promptText] of Object.entries(customPrompts)) {
    const promptConfig = promptsList.find(p => p.key === promptName);
    
    if (!promptConfig) {
      errors[promptName] = [`Invalid prompt name: ${promptName}`];
      allValid = false;
      continue;
    }
    
    const requiredPlaceholders = promptConfig.requiredPlaceholders || promptConfig.required_placeholders || [];
    const validation = validatePrompt(
      promptName,
      promptText,
      requiredPlaceholders,
      promptsList
    );
    
    if (!validation.isValid) {
      errors[promptName] = validation.errors;
      allValid = false;
    }
  }
  
  return {
    isValid: allValid,
    errors
  };
};

/**
 * Format validation errors for display
 * @param {Object} errors - Object mapping prompt names to error arrays
 * @param {Array} editablePromptsList - Optional list of editable prompts
 * @returns {string} Formatted error message
 */
export const formatValidationErrors = (errors, editablePromptsList = null) => {
  if (!errors || Object.keys(errors).length === 0) {
    return '';
  }
  
  // Use provided list or fallback
  let promptsList;
  if (editablePromptsList) {
    promptsList = editablePromptsList;
  } else {
    promptsList = FALLBACK_EDITABLE_PROMPTS;
  }
  
  const errorParts = [];
  for (const [promptName, promptErrors] of Object.entries(errors)) {
    const promptConfig = promptsList.find(p => p.key === promptName);
    const promptDisplayName = promptConfig ? (promptConfig.name || promptName) : promptName;
    
    if (Array.isArray(promptErrors)) {
      if (promptErrors.length === 1) {
        if (promptErrors[0].includes('Invalid prompt name')) {
          errorParts.push(`  - ${promptDisplayName}: ${promptErrors[0]}`);
        } else if (promptErrors[0].includes('cannot be empty')) {
          errorParts.push(`  - ${promptDisplayName}: ${promptErrors[0]}`);
        } else {
          errorParts.push(`  - ${promptDisplayName}: ${promptErrors[0]}`);
        }
      } else {
        const errorMsg = promptErrors.join('; ');
        errorParts.push(`  - ${promptDisplayName}: ${errorMsg}`);
      }
    } else {
      errorParts.push(`  - ${promptDisplayName}: ${promptErrors}`);
    }
  }
  
  return errorParts.join('\n');
};

/**
 * Validate model
 * @param {string} model - Model value to validate
 * @param {Array} availableModels - Array of available models
 * @returns {Object} Validation result
 */
export const validateModel = (model, availableModels) => {
  if (!model) {
    return { isValid: true, error: null };
  }
  
  const validValues = availableModels.map(m => m.value);
  if (!validValues.includes(model)) {
    return {
      isValid: false,
      error: `Invalid model: ${model}. Available models: ${validValues.join(', ')}`
    };
  }
  
  return { isValid: true, error: null };
};

